import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { getFieldDefinition, Operator } from "@/types/queryBuilder";
import { CEFR_LEVELS, COMMON_LANGUAGES } from "@/types/candidate";

interface ValueInputProps {
	fieldKey: string;
	operator: Operator;
	value: string | string[] | number | boolean | null;
	secondValue?: string | null;
	onChange: (value: any, secondValue?: any) => void;
}

export function ValueInput({ fieldKey, operator, value, secondValue, onChange }: ValueInputProps) {
	const { t } = useTranslation();
	const field = getFieldDefinition(fieldKey);
	const [inputValue, setInputValue] = useState('');

	// No input needed for empty/not empty operators
	if (['is_empty', 'is_not_empty'].includes(operator))
		return null;

	// Enum field with single value
	if (field?.type === 'enum' && !['in', 'not_in'].includes(operator))
		return (
			<Select value={value as string || ''} onValueChange={onChange}>
				<SelectTrigger className="w-[160px]">
					<SelectValue placeholder={t("queries.selectValue", "Select value")} />
				</SelectTrigger>
				<SelectContent>
					{field.options?.map((option) => (
						<SelectItem key={option} value={option}>
							{t(`candidates.status.${option}`, option)}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		);

	// Enum field with multiple values (in/not_in)
	if (field?.type === 'enum' && ['in', 'not_in'].includes(operator)) {
		const selectedValues = Array.isArray(value) ? value : [];
		return (
			<div className="flex flex-wrap gap-1 items-center min-w-[200px]">
				{selectedValues.map((v) => (
					<Badge key={v} variant="secondary" className="gap-1">
						{t(`candidates.status.${v}`, v)}
						<X
							className="h-3 w-3 cursor-pointer"
							onClick={() => onChange(selectedValues.filter(x => x !== v))}
						/>
					</Badge>
				))}
				<Select
					value=""
					onValueChange={(v) => {
						if (!selectedValues.includes(v))
							onChange([...selectedValues, v]);
					}}
				>
					<SelectTrigger className="w-[120px] h-7">
						<SelectValue placeholder={t("common.add", "Add")} />
					</SelectTrigger>
					<SelectContent>
						{field.options?.filter(o => !selectedValues.includes(o)).map((option) => (
							<SelectItem key={option} value={option}>
								{t(`candidates.status.${option}`, option)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
		);
	}

	// Array field (skills) - multi-value input
	if (field?.type === 'array') {
		const selectedValues = Array.isArray(value) ? value : [];
		return (
			<div className="flex flex-wrap gap-1 items-center min-w-[200px]">
				{selectedValues.map((v) => (
					<Badge key={v} variant="secondary" className="gap-1">
						{v}
						<X
							className="h-3 w-3 cursor-pointer"
							onClick={() => onChange(selectedValues.filter(x => x !== v))}
						/>
					</Badge>
				))}
				<Input
					className="w-[120px] h-7"
					placeholder={t("queries.addValue", "Add value")}
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && inputValue.trim()) {
							e.preventDefault();
							if (!selectedValues.includes(inputValue.trim()))
								onChange([...selectedValues, inputValue.trim()]);
							setInputValue('');
						}
					}}
				/>
			</div>
		);
	}

	// Date field
	if (field?.type === 'date') {
		if (operator === 'between')
			return (
				<div className="flex gap-2 items-center">
					<Input
						type="date"
						className="w-[150px]"
						value={value as string || ''}
						onChange={(e) => onChange(e.target.value, secondValue)}
					/>
					<span className="text-sm text-muted-foreground">{t("common.and", "and")}</span>
					<Input
						type="date"
						className="w-[150px]"
						value={secondValue || ''}
						onChange={(e) => onChange(value, e.target.value)}
					/>
				</div>
			);

		return (
			<Input
				type="date"
				className="w-[160px]"
				value={value as string || ''}
				onChange={(e) => onChange(e.target.value)}
			/>
		);
	}

	// Language field: language selector + optional CEFR level selector
	if (field?.type === 'language') {
		const needsLevel = !['any_level'].includes(operator);
		return (
			<div className="flex gap-2 items-center">
				<Select
					value={value as string || ''}
					onValueChange={(v) => onChange(v, secondValue)}
				>
					<SelectTrigger className="w-[160px]">
						<SelectValue placeholder={t("queries.selectLanguage", "Select language")} />
					</SelectTrigger>
					<SelectContent>
						{Object.entries(COMMON_LANGUAGES).map(([code, name]) => (
							<SelectItem key={code} value={name}>
								{name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{needsLevel && (
					<Select
						value={secondValue || ''}
						onValueChange={(v) => onChange(value, v)}
					>
						<SelectTrigger className="w-[180px]">
							<SelectValue placeholder={t("queries.selectLevel", "Select level")} />
						</SelectTrigger>
						<SelectContent>
							{Object.entries(CEFR_LEVELS)
								.filter(([k]) => k !== '0')
								.map(([level, label]) => (
									<SelectItem key={level} value={level}>
										{label}
									</SelectItem>
								))}
						</SelectContent>
					</Select>
				)}
			</div>
		);
	}

	// Default: text input
	return (
		<Input
			className="w-[200px]"
			placeholder={t("queries.enterValue", "Enter value")}
			value={value as string || ''}
			onChange={(e) => onChange(e.target.value)}
		/>
	);
}
